import { useState, MouseEvent, useEffect } from 'react'
import { Order } from '@common/components/Table/table.types'
import { GetReadTasksReadTasksGetData, ReadTask, ReadTaskCategory } from '@common/config/api/client'
import { usePaginatedTasks } from '@reader_task_list_view/hooks/usePaginatedTasks'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useDebounce } from '@common/hooks/useDebounce'

interface UseReadTasksManagementHandlersProps {
  category: ReadTaskCategory
  studyId: string
  subjectId?: string
  searchValue?: string
}

export const useReadTasksManagementHandlers = ({
  category,
  studyId,
  subjectId,
  searchValue = '',
}: UseReadTasksManagementHandlersProps) => {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<keyof ReadTask | string>('subject_id')
  const [page, setPage] = useState(0)
  const debouncedSearch = useDebounce(searchValue, 300)

  const [paginationParams, setPaginationParams] = useState<GetReadTasksReadTasksGetData['query']>({
    limit: ROW_PER_PAGE_OPTIONS[2],
    next_cursor: null,
    previous_cursor: null,
    category,
    study_id: studyId,
    subject_id: subjectId,
    asc_order: true,
    order_by_field: 'created_at',
  })

  useEffect(() => {
    setPaginationParams((prev) => ({
      ...prev,
      search: debouncedSearch,
    }))
  }, [debouncedSearch])

  useEffect(() => {
    setPaginationParams((prev) => ({
      ...prev,
      subject_id: subjectId,
    }))
  }, [subjectId])

  const queryKey = [
    'ReadTasksList',
    category,
    `${page}`,
    `${paginationParams.limit}`,
    `${paginationParams.search || ''}`,
    `${subjectId || ''}`,
  ]

  const { data, error, isLoading } = usePaginatedTasks(queryKey, paginationParams)

  const handleRequestSort = (_event: MouseEvent<unknown>, property: keyof ReadTask) => {
    const isAsc = orderBy.split('.')[0] === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)

    setPaginationParams((prev) => ({
      ...prev,
      asc_order: !(orderBy.split('.')[0] === property && order === 'asc'),
      order_by_field: property as string,
    }))
  }

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    if (newPage > 0) {
      setPaginationParams((prev) => ({
        ...prev,
        next_cursor: data?.data?.pagination.next_cursor,
        previous_cursor: null,
      }))
      setPage(page + 1)
    } else if (newPage < 0) {
      setPaginationParams((prev) => ({
        ...prev,
        next_cursor: null,
        previous_cursor: data?.data?.pagination.previous_cursor,
      }))
      setPage(page - 1)
    }
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      next_cursor: null,
      previous_cursor: null,
    }))
    setPage(0)
  }

  return {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
    setPaginationParams,
  }
}
